import { Form, Formik } from 'formik';
import { BsInfoCircle } from 'react-icons/bs';
import { Tooltip as ReactTooltip } from 'react-tooltip';

import FormField from '../FormField';
import { IField } from '../interfaces/IField';
import { createHaggleEventSchema } from './schema/haggle-event.schema';

import { StripeConnectButton } from '@/components/stripe/StripeConnectButton';
import Button from '@/components/ui/button';
import { MAX_PRODUCTS_SELECTION } from '@/components/utils/constants';
import { HaggleType } from '@/components/utils/enums';
import { formatToLocalISO } from '@/components/utils/utils';
import { useUser } from '@/hooks/useUser';
import { InventoryType } from '@/interfaces/api/InventoryType';
import { HaggleEventFormOptions } from '@/interfaces/api/haggle-event/HaggleEventFormOptions.enum';
import { ICreateHaggleEventWithTimeOnForm } from '@/interfaces/api/haggle-event/ICreateHaggleEventPayload';
import { IProduct } from '@/interfaces/api/product/IProduct';
import { UserRole } from '@/interfaces/api/user/UserRole.enum';

const values: ICreateHaggleEventWithTimeOnForm = {
	acceptableMargin: 0,
	startDateTime: '',
	endDateTime: '',
	productIds: [],
	limitCheckoutTime: 5,
	showHaggleBannerOnShopify: 'false',
	freeShippingThreshold: null,
	addAutoShopifyPolicy: 'true',
	customReturnAndExchangePolicy: '',
	shippingPolicy: '',
	upcomingPreviewDate: '',
	followUpOfferValidity: '',
	hostHaggleEventOnShopify: 'true',
	isCSVHaggle: false,
	invitedEmails: [],
	password: null,
	haggleType: null,
	minOrderValue: null,
	minOrderQuantity: null,
};

const formFields: (IField & {
	resources?: IProduct[];
	displayField?: string;
})[] = [
	{
		name: 'startDateTime',
		label: 'Start Date & Time *',
		type: 'datetimepicker',
		placeholder: 'Select Event Start Date & Time',
	},
	{
		name: 'endDateTime',
		label: 'End Date & Time *',
		type: 'datetimepicker',
		placeholder: 'Select Event End Date & Time',
	},
	{
		name: 'acceptableMargin',
		label: 'Max. Discount (in %) *',
		type: 'number',
		placeholder: '15',
	},
	{
		name: 'productIds',
		label: `Select Products (Max ${MAX_PRODUCTS_SELECTION}) *`,
		type: 'multiSelect',
		resources: [],
		displayField: 'title',
	},

	{
		name: 'limitCheckoutTime',
		label: '',
		type: 'number',
		placeholder: '5',
	},
	{
		name: 'freeShippingThreshold',
		label: '',
		type: 'number',
		placeholder: 'Enter amount',
	},
	{
		name: 'showHaggleBannerOnShopify',
		label: '',
		type: 'radio',
		options: [
			{ label: 'Yes', value: 'true' },
			{ label: 'No', value: 'false' },
		],
	},
	{
		name: 'followUpOfferValidity',
		label: '',
		type: 'datetimepicker',
		placeholder: 'Select Date and Time',
	},
	{
		name: 'addAutoShopifyPolicy',
		label: 'Automatically add Store Policies? *',
		type: 'radio',
		options: [
			{ label: 'Yes', value: 'true' },
			{ label: 'No', value: 'false' },
		],
	},
	{
		name: 'customReturnAndExchangePolicy',
		label: 'Enter Custom Return and Exchange Policy',
		type: 'textarea',
		placeholder: 'Custom policy',
	},
	{
		name: 'upcomingPreviewDate',
		label: '',
		type: 'datetimepicker',
		placeholder: 'Select Date and Time',
	},
	{
		name: 'hostHaggleEventOnShopify',
		label: '',
		type: 'radio',
		options: [
			{ label: 'My website', value: 'true' },
			{ label: 'Haggle Hub', value: 'false' },
		],
	},
	{
		name: 'haggleType',
		label: 'Haggle Type',
		type: 'radio',
		options: [
			{ label: 'Public', value: HaggleType.PUBLIC },
			{ label: 'Semi Private', value: HaggleType.SEMI_PRIVATE },
			{ label: 'Private', value: HaggleType.PRIVATE },
		],
	},
	{
		name: 'password',
		label: 'Enter password',
		type: 'password',
		placeholder: 'Enter password for your haggle',
	},

	{
		name: 'invitedEmails',
		label: 'Add Emails to invite (You can add multiple emails)',
		type: 'emailMultiInput',
		resources: [],
		displayField: 'title',
	},
	{
		name: 'minOrderValue',
		label: 'Minimum Order Value ($)',
		type: 'number',
		placeholder: 'Enter minimum order value',
	},
	{
		name: 'minOrderQuantity',
		label: 'Minimum Order Quantity (pcs)',
		type: 'number',
		placeholder: 'Enter minimum order quantity',
	},
	{
		name: 'shippingPolicy',
		label: 'Enter Shipping Policy',
		type: 'textarea',
		placeholder: 'Shipping policy',
	},
];

type PropTypes = {
	handleSubmit: (
		values: ICreateHaggleEventWithTimeOnForm,
	) => Promise<void> | undefined;
	loading: boolean;
	initialValues?: ICreateHaggleEventWithTimeOnForm;
	haggleOption: HaggleEventFormOptions;
	products: IProduct[];
	isBusinessEvent?: boolean;
	inventoryType: InventoryType;
};

const PRODUCTS_IDS_KEY_NAME = 'productIds';
const HaggleEventForm = ({
	handleSubmit,
	loading,
	initialValues = values,
	products,
	haggleOption,
	isBusinessEvent,
	inventoryType,
}: PropTypes) => {
	const { user } = useUser();
	const isVendorPaidUser = !user?.roles?.includes(UserRole.MerchantEmerald);
	const dynamicHaggleEventSchema = createHaggleEventSchema(
		user?.chargesEnabled ?? false,
	);

	const preparedInitialValues = {
		...initialValues,
		addAutoShopifyPolicy:
			inventoryType === InventoryType.CSV
				? 'csv'
				: initialValues.addAutoShopifyPolicy,
		// Force banner to false if user is not vendor paid
		showHaggleBannerOnShopify: !isVendorPaidUser
			? 'false'
			: initialValues.showHaggleBannerOnShopify,
		...(isBusinessEvent &&
			haggleOption === HaggleEventFormOptions.CREATE && {
				haggleType: HaggleType.PUBLIC,
				password: null,
				invitedEmails: [],
			}),
	};

	const fieldsWithProducts = formFields.map((field) => {
		if (field.name === PRODUCTS_IDS_KEY_NAME) {
			return {
				...field,
				resources: products,
				disabled: haggleOption === HaggleEventFormOptions.VIEW,
			};
		} else if (field.name === 'customReturnAndExchangePolicy') {
			// This field will be controlled by the Form values
			return {
				...field,
				disabled: haggleOption === HaggleEventFormOptions.VIEW,
			};
		}
		return {
			...field,
			disabled: haggleOption === HaggleEventFormOptions.VIEW,
		};
	});

	const handleInitialValues = (
		initialValues: ICreateHaggleEventWithTimeOnForm,
	): ICreateHaggleEventWithTimeOnForm => {
		return {
			...initialValues,
			addAutoShopifyPolicy:
				inventoryType === InventoryType.CSV
					? 'csv'
					: initialValues.addAutoShopifyPolicy,
			// Force banner to false if user is not vendor paid
			showHaggleBannerOnShopify: !isVendorPaidUser
				? 'false'
				: initialValues.showHaggleBannerOnShopify,
			startDateTime: initialValues.startDateTime
				? formatToLocalISO(initialValues.startDateTime)
				: '',
			endDateTime: initialValues.endDateTime
				? formatToLocalISO(initialValues.endDateTime)
				: '',
			upcomingPreviewDate: initialValues.upcomingPreviewDate
				? formatToLocalISO(initialValues.upcomingPreviewDate)
				: '',
			followUpOfferValidity: initialValues.followUpOfferValidity
				? formatToLocalISO(initialValues.followUpOfferValidity)
				: '',
			productIds: Array.isArray(initialValues.productIds)
				? initialValues.productIds
				: [],
		};
	};

	return (
		<div className="flex flex-col w-full p-4">
			<h3
				className="font-bold text-xl uppercase text-center"
				data-test="haggle-event-title"
			>
				{haggleOption === HaggleEventFormOptions.UPDATE
					? 'Update Haggle Event'
					: haggleOption === HaggleEventFormOptions.CREATE
					? 'Create new Haggle Event'
					: 'View Haggle Event'}
			</h3>

			<Formik
				initialValues={handleInitialValues(
					isBusinessEvent ? preparedInitialValues : initialValues,
				)}
				onSubmit={(values) => handleSubmit(values)}
				validationSchema={dynamicHaggleEventSchema}
			>
				{({ values }) => (
					<Form className="flex flex-wrap gap-2 justify-between w-full p-4">
						<div className="flex flex-col w-full md:w-[48%]">
							{fieldsWithProducts.slice(0, 2).map((field) => (
								<FormField key={field.name} {...field} />
							))}
						</div>

						<div className="w-full md:w-[48%]">
							<FormField<IProduct>
								key={fieldsWithProducts[2].name}
								{...fieldsWithProducts[2]}
							/>
							<FormField<IProduct>
								key={fieldsWithProducts[3].name}
								{...fieldsWithProducts[3]}
							/>

							{isBusinessEvent && (
								<FormField
									key={fieldsWithProducts[12].name}
									{...fieldsWithProducts[12]}
								/>
							)}

							{[HaggleType.SEMI_PRIVATE, HaggleType.PRIVATE].includes(
								values.haggleType as HaggleType,
							) && (
								<FormField
									key={fieldsWithProducts[13].name}
									{...fieldsWithProducts[13]}
								/>
							)}

							{values.haggleType === HaggleType.PRIVATE && (
								<FormField
									key={fieldsWithProducts[14].name}
									{...fieldsWithProducts[14]}
								/>
							)}
						</div>

						{/* Additional Rules Section */}
						<div className="w-full mt-4">
							<div className="border-t w-full py-4" />
							<h4 className="font-medium text-lg mb-4">Additional Rules</h4>
							<div className="flex flex-wrap gap-2 justify-between w-full">
								<div className="flex flex-col w-full md:w-[48%]">
									<div className="text-sm font-medium mb-2">
										<span className="flex items-center">
											Checkout Time Limit (Minutes) *
											<BsInfoCircle
												className="cursor-pointer ml-2"
												data-tooltip-id="checkout-time-limit-tooltip"
											/>
										</span>
									</div>
									<ReactTooltip
										id="checkout-time-limit-tooltip"
										place="top-start"
										content={`This feature encourages faster conversion,
												once expired, the deal is no longer valid and
												inventory updates automatically.`}
										style={{ whiteSpace: 'pre-line' }}
									/>
									<FormField
										key={fieldsWithProducts[4].name}
										{...fieldsWithProducts[4]}
									/>
									{inventoryType === InventoryType.SHOPIFY && (
										<>
											<div className="text-sm font-medium mb-2">
												<span className="flex items-center">
													Offer Free Shipping for orders above ($)
													<BsInfoCircle
														className="cursor-pointer ml-2"
														data-tooltip-id="free-shipping-threshold-tooltip"
													/>
												</span>
											</div>
											<ReactTooltip
												id="free-shipping-threshold-tooltip"
												place="top-start"
												content={`(Optional) Encourage higher offers and boost
											    by unlocking free shipping after a set sales
												threshold—making negotiations more enticing
												for customers.`}
												style={{ whiteSpace: 'pre-line' }}
											/>
											<FormField
												key={fieldsWithProducts[5].name}
												{...fieldsWithProducts[5]}
											/>

											<div className="text-sm font-medium mb-2">
												<span className="flex items-center">
													Show "Haggle Now" Product Banner *
													<BsInfoCircle
														className="cursor-pointer ml-2"
														data-tooltip-id="haggle-now-banner-tooltip"
													/>
												</span>
											</div>
											<ReactTooltip
												id="haggle-now-banner-tooltip"
												place="top-start"
												content={`Highlight products available for Haggle with
											 	a visible banner on the product list within your
												e-commerce platform, making it easy for customers
												to spot and engage in live negotiations.`}
												style={{ whiteSpace: 'pre-line' }}
											/>
											<FormField
												key={fieldsWithProducts[6].name}
												{...fieldsWithProducts[6]}
												disabled={!isVendorPaidUser}
											/>
										</>
									)}
									<div className="flex flex-col">
										<div className="text-sm font-medium mb-2">
											<span className="flex items-center">
												Follow-up Final Offer Validity
												<BsInfoCircle
													className="cursor-pointer ml-2"
													data-tooltip-id="follow-up-final-offer-validity-tooltip"
												/>
											</span>
										</div>
										<ReactTooltip
											id="follow-up-final-offer-validity-tooltip"
											place="top-start"
											content={`(Optional) Automatically send an email
												to customers who didn't secure a deal during
												the Haggle event, giving them another chance
												to make an offer before the deal expires`}
											style={{ whiteSpace: 'pre-line' }}
										/>

										<FormField
											key={fieldsWithProducts[7].name}
											{...fieldsWithProducts[7]}
										/>

										{isBusinessEvent && (
											<>
												<FormField
													key={fieldsWithProducts[15].name}
													{...fieldsWithProducts[15]}
												/>
												<FormField
													key={fieldsWithProducts[16].name}
													{...fieldsWithProducts[16]}
												/>
											</>
										)}
									</div>
								</div>
								<div className="flex flex-col w-full md:w-[48%]">
									<h5 className="font-medium mb-2">
										<span className="flex items-center">
											Return and Exchange Policy
											<BsInfoCircle
												className="cursor-pointer ml-2"
												data-tooltip-id="store-policy-tooltip"
											/>
										</span>
									</h5>
									<ReactTooltip
										id="store-policy-tooltip"
										place="top-start"
										content={`Easily import your existing return and exchange
											    policy from your e-commerce platform. If you choose NO,
												you can manually set your own policy for this event.`}
										style={{ whiteSpace: 'pre-line' }}
									/>
									{inventoryType === InventoryType.SHOPIFY && (
										<FormField
											key={fieldsWithProducts[8].name}
											{...fieldsWithProducts[8]}
										/>
									)}
									<FormField
										key={fieldsWithProducts[9].name}
										{...fieldsWithProducts[9]}
										disabled={
											values.addAutoShopifyPolicy === 'true' ||
											haggleOption === HaggleEventFormOptions.VIEW
										}
									/>
									{inventoryType === InventoryType.CSV && (
										<FormField
											key={fieldsWithProducts[17].name}
											{...fieldsWithProducts[17]}
										/>
									)}
									<div className="text-sm font-medium mb-2">
										<span className="flex items-center">
											Preview upcoming Haggle from
											<BsInfoCircle
												className="cursor-pointer ml-2"
												data-tooltip-id="upcoming-preview-date-tooltip"
											/>
										</span>
									</div>
									<ReactTooltip
										id="upcoming-preview-date-tooltip"
										place="top-start"
										content={`(Optional) Give your customers a sneak peek at
											    upcoming Haggle events by showcasing scheduled
												products before they go live. Build anticipation
												and drive engagement before the event starts!`}
										style={{ whiteSpace: 'pre-line' }}
									/>
									<FormField
										key={fieldsWithProducts[10].name}
										{...fieldsWithProducts[10]}
									/>
									{inventoryType === InventoryType.SHOPIFY && (
										<>
											<div className="text-sm font-medium mb-2">
												<span className="flex items-center">
													Host Haggle Event on: *
													<BsInfoCircle
														className="cursor-pointer ml-2"
														data-tooltip-id="host-haggle-event-on-shopify-tooltip"
													/>
												</span>
											</div>
											<ReactTooltip
												id="host-haggle-event-on-shopify-tooltip"
												place="top-start"
												content={`Choose this option to run the Haggle event directly
												on your own website or on Haggle's platform.`}
												style={{ whiteSpace: 'pre-line' }}
											/>
											<FormField
												key={fieldsWithProducts[11].name}
												{...fieldsWithProducts[11]}
											/>

											{values.hostHaggleEventOnShopify === 'false' && (
												<div className="w-full">
													<StripeConnectButton isSizeCompact={true} />
												</div>
											)}
										</>
									)}
								</div>
							</div>
						</div>

						<div className="flex flex-col w-full md:w-[48%]"></div>

						{haggleOption !== HaggleEventFormOptions.VIEW && (
							<>
								<div className="border w-full" />
								<Button
									type="submit"
									className="w-full my-2 bg-purpleBrand-500 hover:bg-purpleBrand-600"
									shape="rounded"
									data-test="submit-haggle-button"
									isLoading={loading}
									disabled={loading}
								>
									Submit
								</Button>
							</>
						)}
					</Form>
				)}
			</Formik>
		</div>
	);
};

export default HaggleEventForm;
